import { useAppContext } from "@context/app";
import Button from "@elements/button";
import Modal from "@modules/layout/modal";
import DeliveryIcon from "@svg/DeliveryIcon";
import PathIcon from "@svg/PathIcon";
import { IMediaInput, IProductDetailModel, ISkuModel } from "@types";
import { useIsMount } from "@utils/helpers";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { convertNumber, convertStringNumber, toArabicNumber } from "@utils/digits";
import Carousel from "@elements/carousel";
import { SwiperSlide } from "swiper/react";
import ImagesSlider from "@modules/productDetail/imagesSlider";
import { segmentEvent } from "@utils/segment";
import { productViewed_fromPage } from "@utils/constants";
import Loading from "assets/lottie/loading";
import Image from "next/legacy/image";
import PlayIcon from "@svg/bigPlayIcon";
import VideoActionCard from "@elements/videoActionCard";
import Player from "@elements/player";
import VideoProductCard from "@elements/videoProductCard";
import useEvents from "@hooks/useEvents";
import useParentSize from "@hooks/useParentSize";
import ImagePrepare from "@elements/imagePrepare";
import ProductTags from "@modules/productDetail/productTags";
import RelatedProduct from "@modules/productDetail/relatedProduct";
import useProductDetail from "@hooks/useProductDetail";
import FreeDelivery from "@elements/freeDelivery";

interface Props {
  slug: string;
  q: string;
  source: string;
  sourceEntity: string;
  sourceSlug: string;
  section: string;
  sectionEntity: string;
  sectionSlug: string;
  data?: IProductDetailModel;
}

const ProductDetail: React.FC<Props> = ({ data, source, sourceSlug, sourceEntity, section, sectionEntity, sectionSlug }) => {
  const t = useTranslations();
  const { skuVideoPlayed } = useEvents()
  const { locale, query, push } = useRouter();
  const { _data, setData, isLoading, showSuccess, showBuyLimitMsg, handleAddToCart, handleCancelNotifySku, handleNotifySku, selectedSku, setSelectedSku, addToCartState, setShowSuccess } = useProductDetail()
  const [openImagesModal, setOpenImagesModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<IMediaInput | null>(null);
  const [showVideo, setShowVideo] = useState(false)
  const isMount = useIsMount();
  const {
    cart,
    decimal,
    currency,
    country,
    user,
    area,
    setEventProperties,
    syncData,
    freeShipping
  } = useAppContext();
  const [size, parentRef] = useParentSize()
  let default_sku = "";
  if (typeof query.default_sku === "string") {
    default_sku = query.default_sku;
  } else if (typeof query.sku_id === "string") {
    default_sku = query.sku_id;
  }

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 10);

  }, []);
  useEffect(() => {
    if (data) {
      setData(data)
      setEventProperties({
        from_page: "product_details",
        from_entity: "product",
        from_entity_slug: data.slug || "",
        from_page_section: "",
        from_page_section_entity: "",
        from_page_section_slug: "",
      })

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])


  useEffect(() => {
    if (_data?.skus?.length) {
      const _sku = default_sku ? _data.skus.find((sku) => sku.id === Number(default_sku)) ?? _data.skus.find((sku) => sku.id === _data.default_sku_id) : _data.skus.find((sku) => sku.id === _data.default_sku_id);
      setSelectedSku(_sku || null);
      setSelectedImage(_sku?.medias[0] || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_data]);



  useEffect(() => {
    if (_data && area && syncData && !isMount) {
      if (source && productViewed_fromPage.includes(source)) {
        handleEvent()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_data, area, syncData])


  const handleEvent = () => {
    if (_data?.skus.length) {
      const _sku = _data.skus.find((sku) => sku.id === _data.default_sku_id);
      segmentEvent("Product Viewed", {
        product_id: _data.id,
        product_slug: _data.slug,
        default_sku_id: _data.default_sku_id,
        default_sku_slug: _sku?.slug,
        brand_id: _data.brand.id,
        brand_slug: _data.brand.slug,
        category_id: _data.category.id,
        category_slug: _data.category.slug,
        subcategory_id: _data.subcategory.id,
        subcategory_slug: _data.subcategory.slug,
        sku_old_price: _sku?.price.old_price || _sku?.price.price,
        sku_new_price: _sku?.price.price,
        currency: country?.currency_iso.toUpperCase(),
        user_id: user.data?.id || -1,
        country_id: country?.id,
        country_slug: country?.slug,
        area_id: area?.id,
        area_slug: area?.slug,
        out_of_stock: _sku?.buy_limit ? false : true,
        language: locale?.includes("en") ? "English" : "Arabic",
        from_page: source,
        from_entity: sourceEntity,
        from_entity_slug: sourceSlug,
        from_page_section: section,
        from_page_section_entity: sectionEntity,
        from_page_section_slug: sectionSlug,
      })
    }

  }


  const handleSelectedSku = (sku: ISkuModel) => {
    window.scrollTo(0, 0);
    setSelectedSku(sku);
    setSelectedImage(sku.medias?.[0] || null);
  };

  const onClose = () => {
    setShowVideo(false)
  }

  const source_video = {
    from_page: "product_details",
    from_entity: "sku",
    from_entity_slug: selectedSku?.slug || "",
    from_page_section: "",
    from_page_section_entity: "",
    from_page_section_slug: "",
  }

  const handlePlayVideo = () => {
    if (selectedSku) {
      skuVideoPlayed(source_video, selectedSku, "", { id: data?.id || -1, slug: data?.slug || "" })
    }
  }

  let skuContent;
  if (selectedSku) {
    if (selectedSku.buy_limit) {
      skuContent = (<div className="w-full md:mb-10 sm:mb-5 mb-5 md:flex hidden flex-wrap items-center">
        <div className="md:px-0 sm:p-0 p-0 w-[max-content] mb-2">
          <Button
            size="slim"
            onClick={() =>
              selectedSku.buy_limit <=
                (cart.find((p) => p.id === selectedSku.id)?.qty ||
                  0)
                ? showBuyLimitMsg()
                : handleAddToCart()
            }
            isDisabled={addToCartState}
            className="font-bold text-lg capitalize h-12 rounded-full w-44"
          >
            {t("client_buttons_addtocart")}
          </Button>
        </div>
        {selectedSku.buy_limit ? (
          <div className=" px-3 mb-2">
            {selectedSku?.price.old_price && (
              <div className=" flex items-center left-to-right mb-2 ">
                <s className=" font-normal text-base px-1 ">
                  {convertStringNumber(locale, selectedSku.price.old_price.toFixed(decimal))}
                </s>
                <div
                  className="w-7 h-7 rounded-lg flex justify-center items-center"
                  style={{
                    backgroundColor:
                      selectedSku.price.badge?.background_color || "#FFD600",
                    color: selectedSku.price.badge?.text_color,
                  }}
                >
                  <p className=" font-normal text-[12px] ">
                    {convertStringNumber(locale, selectedSku.price.badge?.text)}
                  </p>
                </div>

              </div>)}
            <p className="font-bold text-base flex items-center left-to-right">
              <span
                style={{ direction: "rtl" }}
                className="text-left"
              >
                {currency}{" "}
              </span>

              <span className="px-1 text-[#333] font-bold text-2xl">
                {convertStringNumber(locale, selectedSku.price.price.toFixed(decimal))}
              </span>
            </p>
            {selectedSku.price.vat ? (
              <p className=" text-[#999] text-sm font-normal">
                {t("client_productdetails_inclusiveofvat")}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>)
    } else {
      if (selectedSku.will_notify) {
        skuContent = (<div className="w-full md:mb-10 sm:mb-5 mb-5 md:flex hidden items-center">
          <p className=" font-normal text-[#999] w-44 leading-8">{t("client_productdetails_notifymeonceavailable_message")}</p>
          <Button
            size="slim"
            theme="lightBlue"
            className="font-bold text-lg capitalize h-12 rounded-full !w-[130px] mx-2"
            onClick={() => handleCancelNotifySku(selectedSku.id)}
          >
            {t("client_buttons_cancel")}
          </Button>
        </div>)
      } else {
        skuContent = (<div className="w-full md:mb-10 sm:mb-5 mb-5">
          <Button
            size="slim"
            theme="lightBlue"
            className="font-bold text-lg capitalize h-12 rounded-full"
            onClick={() => handleNotifySku(selectedSku.id)}
          >
            {t("client_productdetails_notifymeonceavailable")}
          </Button>
        </div>)
      }
    }
  }


  let mobileSkuContent;
  if (selectedSku) {
    if (selectedSku.buy_limit) {
      mobileSkuContent = (<div className="w-full flex justify-between space-x-2 ">
        <div className=" w-max max-w-[50%] ">
          {selectedSku.price.old_price && (
            <div className=" flex items-center !justify-end left-to-right mb-1 ">
              <s className=" font-normal text-sm px-1 ">
                {convertStringNumber(locale, selectedSku.price.old_price.toFixed(decimal))}
              </s>
              <div
                className="w-7 h-7 rounded-lg flex justify-center items-center"
                style={{
                  backgroundColor:
                    selectedSku.price.badge?.background_color || "#FFD600",
                  color: selectedSku.price.badge?.text_color,
                }}
              >
                <p className=" font-normal text-[12px] ">
                  {selectedSku.price.badge?.text}
                </p>
              </div>

            </div>)}
          <p className=" font-bold text-base flex items-center left-to-right mb-1 space-x-1">
            <span
              dir="rtl"
              className="text-left"
            >
              {currency}{" "}
            </span>

            <span className=" text-[#333] font-bold text-2xl">
              {convertStringNumber(locale, selectedSku.price.price.toFixed(decimal))}
            </span>
          </p>
          {selectedSku.price.vat ? (
            <p className=" text-[#999] text-[10px] font-normal ">
              {t("client_productdetails_inclusiveofvat")}
            </p>
          ) : null}
        </div>
        <div className="w-max max-w-[50%] flex justify-center">
          <Button
            size="slim"
            onClick={() =>
              selectedSku.buy_limit <=
                (cart.find((p) => p.id === selectedSku.id)?.qty ||
                  0)
                ? showBuyLimitMsg()
                : handleAddToCart()
            }
            isDisabled={addToCartState}
            className="font-bold text-base capitalize h-12 rounded-full !w-full !max-w-[184px]"
          >
            {t("client_buttons_addtocart")}
          </Button>
        </div>
      </div>)
    } else {
      if (selectedSku.will_notify) {
        mobileSkuContent = (<div className="w-full flex  space-x-2">
          <p className="w-1/2 font-normal text-[#999] text-sm ">{t("client_productdetails_notifymeonceavailable_message")}</p>
          <Button
            size="slim"
            theme="lightBlue"
            className="font-bold text-base capitalize h-12 rounded-full !w-1/2"
            onClick={() => handleCancelNotifySku(selectedSku.id)}
          >
            {t("client_buttons_cancel")}
          </Button>
        </div>)
      } else {
        mobileSkuContent = (<div className="w-full ">
          <Button
            size="slim"
            theme="lightBlue"
            className="font-bold text-base capitalize h-12 rounded-full !w-full"
            onClick={() => handleNotifySku(selectedSku.id)}
          >
            {t("client_productdetails_notifymeonceavailable")}
          </Button>
        </div>)
      }
    }
  }

  const totalPrice = cart.reduce((previousValue, currentValue) => {
    return previousValue + currentValue.qty * (currentValue.raw_price || currentValue.price);
  }, 0);

  const subtotal = locale?.includes("ar-") ? `${totalPrice.toFixed(decimal)} ${currency}` : `${currency} ${totalPrice.toFixed(decimal)}`;
  const replacedText = t("client_productdetails_cartsubtotal").replace("@", `<span class="text-textDark text-sm font-normal ">${subtotal}</span>`);
  return (
    <div className="w-full  max-w-[1200px] m-auto px-2">
      {isLoading && <Loading isCenter />}
      {data && (
        <>
          <Modal
            open={openImagesModal}
            onClose={() => setOpenImagesModal(false)}
            cancelable
          >
            <div className="w-max max-w-lg h-max flex justify-center items-center mx-auto bg-white rounded-lg" >
              <ImagesSlider images={selectedSku?.medias || []} />
            </div>
          </Modal>
          <Modal
            open={showSuccess}
            onClose={() => setShowSuccess(false)}
            cancelable>
            <div className=" w-screen max-w-sm bg-white min-h-[300px] rounded-2xl mx-auto p-5 flex flex-col gap-5">
              <div className=" w-full flex flex-wrap items-center justify-center">
                <p className=" text-textDark font-normal ">{t("client_productdetails_addedtocart")}</p>
              </div>
              <div className=" relative w-[72px] h-[72px] mx-auto ">
                <Image src="/imgs/Indicator.webp" layout="fill" alt="" />
              </div>
              <div className=" w-full flex flex-wrap items-center justify-center">
                <bdi className=" text-textDark text-sm font-normal ">{t("client_productdetails_success_addedtocart")}</bdi>
              </div>
              {freeShipping ? <div className=" w-full flex flex-wrap items-center justify-center">
                <bdi className=" text-textLight text-sm font-normal ">
                  <div dangerouslySetInnerHTML={{ __html: replacedText }} />
                </bdi>
              </div> : null}
              <div className="w-full">
                <FreeDelivery />
              </div>

              <div className="w-full flex flex-wrap justify-center space-x-3">
                <Button
                  size="slim"
                  className="h-12 font-extrabold text-base rounded-full w-[calc(50%-12px)] max-w-[166px]"
                  theme="lightBlue"
                  onClick={() => setShowSuccess(false)}
                >
                  {t("client_buttons_keepshopping")}
                </Button>
                <Button
                  size="slim"
                  className="h-12 font-extrabold text-base rounded-full w-[calc(50%-12px)] max-w-[166px]"
                  onClick={() => push("/cart")}
                >
                  {t("client_buttons_gotocart")}
                </Button>
              </div>
            </div>
          </Modal>
          {showVideo && selectedSku?.video && <Modal open={showVideo} onClose={onClose} cancelable={false} isCenter>
            <div className="md:w-[383px] sm:w-screen xs:w-screen w-screen  max-w-[383px]  h-max max-h-[732px] flex flex-col justify-center items-center relative px-2">
              <div className="w-full px-2 md:static absolute z-10 inset-0 h-max" >
                <VideoActionCard index={0} onClose={onClose} isList={false} sku={selectedSku} />
              </div>
              <div className="w-full  overflow-hidden" tabIndex={1}>
                <div className=" w-full pt-[140%] relative">
                  <div className=" absolute inset-0 w-full h-full">
                    <Player autoPlayOnSrcChange subtitle="" sources={[selectedSku.video]} poster={selectedSku.video.VCover} loop={true} />
                  </div>
                  <div className="w-full bottom-0 left-0 p-4 absolute  cart_video rounded-b-2xl" >
                    <div className="w-full min-h-[88px] h-max bg-white rounded-2xl p-3">
                      <VideoProductCard sku={selectedSku} productName={data.name} productSlug={data.slug} productId={data.id} category={data.category} subcategory={data.subcategory} brand={data.brand} />
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </Modal>}
          <div className="flex flex-wrap mt-10 justify-center ">
            <div className="flex  mb-5 md:w-max  w-full justify-center h-max lmx:sticky !top-[190px] ">
              <div className="w-[96.5px] md:block sm:hidden hidden ">
                {selectedSku?.medias.map(
                  (image: IMediaInput, index: number) =>
                    index <= 2 && (
                      <div className="w-full mb-2" key={image.url}>
                        <div
                          onClick={() => setSelectedImage(image)}
                          className={`transition-all duration-300 ease-linear w-full pt-[100%] relative   overflow-hidden cursor-pointer`}
                        >
                          <div className="w-full h-full inset-0 absolute">
                            <div className={`w-full h-full relative p-2 border-2 rounded-2xl ${selectedImage?.url === image.url
                              ? " border-[#008AF6]"
                              : "border-[#eee]"
                              }`}>
                              <ImagePrepare media={image} alt={data.name} width={80} />
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                )}
                {selectedSku && selectedSku.medias.length > 3 ? (
                  <div className="w-full">
                    <div
                      onClick={() => setOpenImagesModal(true)}
                      className={` cursor-pointer  w-full pt-[100%] relative `}
                    >
                      <div className=" w-full h-full p-2 border-2 absolute inset-0 border-[#eee] rounded-2xl flex justify-center items-center">
                        <p className=" text-[#008AF6] font-normal text-xl">{`+ ${locale?.includes("ar")
                          ? toArabicNumber(selectedSku.medias.length - 3)
                          : selectedSku.medias.length - 3
                          }`}</p>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className=" max-w-[425px] w-[100vw] flex-1 px-2">
                <div className="w-full">
                  <div
                    className={`transition-all duration-300 ease-linear w-full pt-[100%] relative  overflow-hidden cursor-pointer `}
                  >
                    <div className="w-full h-full inset-0 absolute">
                      <div className={`w-full h-full relative p-2 border-2 rounded-2xl border-[#eee] transition-all duration-300 ease-linear`} ref={parentRef}>
                        {selectedImage ? <ImagePrepare media={selectedImage} alt={data.name} width={size.width} /> : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="md:hidden md:w-0  w-full flex mb-5">
              <Carousel>
                {selectedSku?.medias.map(
                  (image: IMediaInput) => (
                    <SwiperSlide key={image.url} className="w-max">
                      <div className="w-[100px] m-1">
                        <div
                          onClick={() => setSelectedImage(image)}
                          className={` transition-all duration-300 ease-linear w-full pt-[100%] relative   overflow-hidden cursor-pointer`}
                        >
                          <div className="w-full h-full inset-0 absolute">
                            <div className={`w-full h-full relative p-2 border-2 rounded-2xl ${selectedImage?.url === image.url
                              ? " border-[#008AF6]"
                              : "border-[#eee]"
                              }`}>
                              <ImagePrepare media={image} alt={data.name} width={100} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </SwiperSlide>
                  )
                )}
              </Carousel>
            </div>
            <div className="lmx:w-[calc(100%-530px)] w-full md:px-5 sm:px-1 px-1">
              <div className="w-full pb-10 h-[max-content]">
                <div className=" lmx:min-h-[430px] ">
                  <div className="w-full mb-3 flex items-center">
                    <Link
                      href={`/product?category_id=${data.category.id}`}
                      prefetch={false}
                      passHref
                      onClick={() => {
                        segmentEvent("Category Viewed", {
                          user_id: user.data?.id || -1,
                          country_id: country?.id,
                          country_slug: country?.slug,
                          language: locale?.includes("en") ? "English" : "Arabic",
                          category_id: data.category.id,
                          category_slug: data.category.slug,
                          from_page: "product_details",
                          from_entity: "product",
                          from_entity_slug: data.slug || "",
                          from_page_section: "",
                          from_page_section_entity: "",
                          from_page_section_slug: "",
                        });
                      }}
                      legacyBehavior>

                      <h3 className="text-[#008AF6] text-base">{data.category.name}</h3>

                    </Link>
                    {data.subcategory ? (
                      <PathIcon
                        stroke="#999"
                        className={`${locale?.includes("ar") ? "rotate-180" : " rotate-0"
                          } mx-2`}
                      />
                    ) : null}
                    {data.subcategory ? (
                      <Link
                        href={`/product?category_id=${data.category.id}&subcategory_id=${data.subcategory.id}`}
                        prefetch={false}
                        passHref
                        onClick={() => {
                          segmentEvent("Category Viewed", {
                            user_id: user.data?.id || -1,
                            country_id: country?.id,
                            country_slug: country?.slug,
                            language: locale?.includes("en") ? "English" : "Arabic",
                            category_id: data.subcategory.id,
                            category_slug: data.subcategory.slug,
                            from_page: "product_details",
                            from_entity: "product",
                            from_entity_slug: data.slug || "",
                            from_page_section: "",
                            from_page_section_entity: "",
                            from_page_section_slug: "",
                          });
                        }}
                        legacyBehavior>

                        <h4 className="text-[#008AF6] text-base">
                          {data.subcategory.name}
                        </h4>

                      </Link>
                    ) : null}
                  </div>
                  <div className="w-full mb-5">
                    <h1 className=" font-extrabold text-2xl leading-10">
                      {convertStringNumber(locale, data.name)}
                    </h1>
                  </div>
                  {selectedSku?.notice && (
                    <div className="w-full md:mb-10 sm:mb-3 mb-3">
                      <div className=" flex flex-wrap items-center md:w-max sm:w-full w-full max-w-full  h-max py-3 bg-[#F5F5F5] font-medium rounded-xl px-3">
                        <DeliveryIcon />
                        <span className="px-1">{selectedSku.notice.title}</span>
                        <p className="flex sm:px-3 xs:px-0 px-0 md:py-0 sm:py-3 py-3 sm:w-max xs:w-full w-full">
                          <span className=" ">
                            {convertNumber(locale, selectedSku.notice.message.split("**")[0])}
                          </span>
                          <bdi className="px-1 font-extrabold">
                            {convertNumber(locale, selectedSku.notice.message.split("**")[1])}
                          </bdi>
                        </p>
                      </div>
                    </div>
                  )}
                  {data.skus.length && (data.skus.length > 1 || (data.skus.length === 1 && data.skus[0].name !== data.name)) && (
                    <div className="w-full md:mb-10 sm:mb-5 mb-5 flex flex-wrap">
                      <Carousel>
                        {data.skus?.map((sku: ISkuModel) => (
                          <SwiperSlide className="w-max" key={sku.id}>
                            <div
                              onClick={() => {
                                handleSelectedSku(sku);
                              }}
                              className={`min-w-[110px]  transition-all duration-300 ease-linear max-w-[240px] h-12 border-2 ${sku.id === selectedSku?.id
                                ? "border-[#008AF6] text-[#008AF6]"
                                : ""
                                } mr-2 rounded-full overflow-hidden flex items-center cursor-pointer mb-2 px-1`}
                            >
                              <div className=" w-8 h-8 relative rounded-lg overflow-hidden">
                                <ImagePrepare media={sku.medias[0]} alt={data.name} width={32} />
                              </div>
                              <p
                                className={`m-1 flex-1 font-normal text-sm ${sku.id === selectedSku?.id
                                  ? "text-[#008AF6]"
                                  : "text-[#777]"
                                  }  text-ellipsis overflow-hidden whitespace-nowrap`}
                              >
                                {convertStringNumber(locale, sku.name)}
                              </p>
                            </div>
                          </SwiperSlide>
                        ))}
                      </Carousel>
                    </div>
                  )}

                  {skuContent}
                </div>

                {selectedSku?.video && <div className="w-full md:mt-5 sm:mt-0 mt-0  max-w-[556px] mb-10">
                  <div className="w-full pt-[50%] relative">
                    <div className="w-full h-full absolute inset-0">
                      <div className="w-full h-full relative overflow-hidden rounded-2xl cursor-pointer" onClick={() => { setShowVideo(true); handlePlayVideo() }}>
                        <Image src={selectedSku.video.HCover} alt={data.name} layout="fill" loading="lazy" sizes="100vw" objectFit="cover" />
                        <div className=" w-16 h-16 bg-white rounded-full absolute top-[calc(50%-32px)] left-[calc(50%-32px)] flex justify-center items-center">
                          <PlayIcon className=" w-5" width={20} />
                        </div>
                      </div>
                    </div>
                  </div>

                </div>}

                <div className="w-full md:mt-5 sm:mt-0 mt-0">
                  <p className=" text-xl font-extrabold text-[#333] mb-3">
                    {t("client_general_description")}
                  </p>
                  <h3 className="text-[#777] text-lg">{convertStringNumber(locale, data.description)}</h3>
                </div>
                <div className="w-full mt-10">
                  <div className=" mb-3 flex">
                    <p className=" font-normal text-base text-[#999] min-w-[100px] w-[max-content] ">
                      {t("client_general_brand")}
                    </p>
                    <h3 className="text-[#333]  font-normal text-base">
                      {data.brand.name}
                    </h3>
                  </div>
                  {data.attributes.map((attribute) => (
                    <div key={`attribute-${attribute.name}`} className=" mb-3 flex">
                      <p className=" font-normal text-base text-[#999] min-w-[100px] w-[max-content] ">
                        {attribute.name}
                      </p>
                      <p className="text-[#333]  font-normal text-base">
                        {attribute.value}
                      </p>
                    </div>
                  ))}
                </div>
                <RelatedProduct data={data.related} sourceSlug={_data?.slug} />
                <div className="w-full mt-5 px-2">
                  <ProductTags tags={data.tags} />
                </div>
              </div>
            </div>
          </div>
          <div className="md:hidden block fixed bottom-0 p-4 min-h-[112px] h-max bg-white left-0 w-full z-[15] rounded-t-[32px] shadow-[0px_-8px_32px_0px_rgba(0,0,0,0.12)]">
            {mobileSkuContent}
          </div>
        </>
      )
      }
    </div >
  );
};

export default ProductDetail;
